import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack } from '@mui/material';

import StackedBar from '../Bar/StackedBar';
import calculateBarChartValues from '../ChartBackground/calculateBarChartValues';
import ChartBackground from '../ChartBackground/ChartBackground';

export interface StackedBarChartProps {
  stackLabels: Array<{ name: string; color: string }>;
  showTotals?: boolean;
  bars: {
    label: string;
    stacks: Array<{ stackLabel: string; value: number }>;
    comparisonValue?: string | number;
  }[];
  scrollable?: boolean;
  hideGridLabels?: boolean;
  withRelativeLabels?: boolean;
}

const StackedBarChart: FC<StackedBarChartProps> = ({
  stackLabels,
  bars,
  showTotals,
  scrollable,
  hideGridLabels,
  withRelativeLabels,
}) => {
  const { t } = useTranslation('followUp');
  const { totalValue, calculatedMaxValue, totalsByStack } = useMemo(
    () =>
      calculateBarChartValues({
        bars,
        showTotals,
        relativeValues: withRelativeLabels,
      }),
    [bars, showTotals],
  );
  return (
    <Box
      sx={{
        height: '100%',
        padding: 4,
        width: '100%',
      }}
    >
      <ChartBackground
        maxValue={calculatedMaxValue}
        isRelative={withRelativeLabels}
        hideGridLabels={hideGridLabels}
      >
        <Stack
          direction="row"
          sx={{
            height: '100%',
            gap: scrollable ? 3.75 : 1,
          }}
        >
          {showTotals && totalsByStack && (
            <StackedBar
              bar={{
                label: t('statistics.total'),
                stacks: Object.entries(totalsByStack).map(
                  ([stackLabel, value]) => ({
                    stackLabel,
                    value,
                  }),
                ),
              }}
              stackLabels={stackLabels}
              calculatedMaxValue={calculatedMaxValue}
              totalValue={totalValue}
              condensed={!scrollable}
            />
          )}
          {bars.map((bar) => (
            <StackedBar
              key={bar.label}
              bar={bar}
              stackLabels={stackLabels}
              totalValue={totalValue}
              calculatedMaxValue={
                withRelativeLabels ? totalValue : calculatedMaxValue
              }
              condensed={!scrollable}
            />
          ))}
        </Stack>
      </ChartBackground>
    </Box>
  );
};

export default StackedBarChart;
